import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  AlgorithmInfo,
  DataObjectOutputType,
  FormLabel,
  getMapBy,
  useDataObjectVersionMetadataVersionsQuery,
} from '@atlas-frontend/sdk';
import { Alert, Flex, Select } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import translations from './AlgorithmsDetails.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'visual-validation-algorithms-details',
);
addTranslations(translations);

export const AlgorithmsDetails = ({
  versionId,
  outputType,
  initialAlgorithmCode,
  initialAlgorithmTargetSequenceNumber,
}: {
  versionId: number;
  outputType?: DataObjectOutputType;
  initialAlgorithmCode?: string;
  initialAlgorithmTargetSequenceNumber?: number | null;
}) => {
  const { t } = useTranslation();
  const { data = [], isFetching } = useDataObjectVersionMetadataVersionsQuery({
    id: versionId,
    type: 'ALGORITHM',
  });

  const [selectedMetadataVersionId, setSelectedMetadataVersionId] = useState<
    number | undefined
  >();
  const [selectedTargetId, setSelectedTargetId] = useState<
    number | undefined
  >();

  const selectedMetadataVersion = useMemo(
    () => data.find(({ id }) => id === selectedMetadataVersionId),
    [data, selectedMetadataVersionId],
  );

  const selectedSnapshot = selectedMetadataVersion?.snapshot;
  const selectedAlgorithm =
    selectedSnapshot && selectedSnapshot.status !== 'unavailable'
      ? selectedSnapshot.data
      : undefined;

  const algorithmOptions = useMemo(
    () =>
      data.map(({ id, code, snapshot }) => {
        const algorithm =
          snapshot.status !== 'unavailable' ? snapshot.data : undefined;
        return {
          label: [algorithm?.code ?? code, algorithm?.description]
            .filter(Boolean)
            .join(' – '),
          value: id,
        };
      }),
    [data],
  );

  const targetOptions = useMemo(
    () =>
      (selectedAlgorithm?.targets ?? []).map((t) => ({
        label: t.sequenceNumber,
        value: t.id,
      })),
    [selectedAlgorithm],
  );

  const displayedAlgorithm = useMemo(
    () =>
      selectedAlgorithm
        ? {
            ...selectedAlgorithm,
            targets:
              selectedTargetId !== undefined
                ? selectedAlgorithm.targets.filter(
                    (t) => t.id === selectedTargetId,
                  )
                : selectedAlgorithm.targets,
          }
        : selectedAlgorithm,
    [selectedAlgorithm, selectedTargetId],
  );

  const sourcesById = useMemo(
    () => getMapBy(selectedAlgorithm?.sources ?? [], 'id'),
    [selectedAlgorithm],
  );

  const mappingsById = useMemo(
    () => getMapBy(selectedAlgorithm?.mappings ?? [], 'id'),
    [selectedAlgorithm],
  );

  const handleAlgorithmChange = (id: number) => {
    setSelectedMetadataVersionId(id);
    setSelectedTargetId(undefined);
  };

  useEffect(() => {
    setSelectedMetadataVersionId(undefined);
    setSelectedTargetId(undefined);
  }, [versionId]);

  useEffect(() => {
    if (!initialAlgorithmCode || !data.length) return;

    const match = data.find(
      ({ code, snapshot }) =>
        code === initialAlgorithmCode ||
        (snapshot.status !== 'unavailable' &&
          snapshot.data.code === initialAlgorithmCode),
    );
    if (!match) return;
    setSelectedMetadataVersionId(match.id);

    if (
      initialAlgorithmTargetSequenceNumber &&
      match.snapshot.status !== 'unavailable'
    ) {
      const target = match.snapshot.data.targets.find(
        (t) => t.sequenceNumber === initialAlgorithmTargetSequenceNumber,
      );
      if (target) setSelectedTargetId(target.id);
    }
  }, [data, initialAlgorithmCode, initialAlgorithmTargetSequenceNumber]);

  return (
    <Flex vertical gap="middle">
      <Flex gap="middle" align="top">
        <Flex vertical gap="small">
          <FormLabel htmlFor="algorithms-details-algorithm-select">
            {t('algorithm')}
          </FormLabel>
          <StyledSelect
            id="algorithms-details-algorithm-select"
            value={selectedMetadataVersionId}
            options={algorithmOptions}
            onChange={handleAlgorithmChange}
            loading={isFetching}
            allowClear
            disabled={!!initialAlgorithmCode}
          />
        </Flex>
        <Flex vertical gap="small">
          <FormLabel htmlFor="algorithms-details-target-select">
            {t('target')}
          </FormLabel>
          <StyledSelect
            id="algorithms-details-target-select"
            value={selectedTargetId}
            options={targetOptions}
            onChange={setSelectedTargetId}
            disabled={
              !selectedAlgorithm ||
              initialAlgorithmTargetSequenceNumber !== undefined
            }
            allowClear
          />
        </Flex>
      </Flex>
      {selectedMetadataVersion?.snapshot.status === 'recovered' && (
        <Alert type="warning" showIcon message={t('detailsRecovered')} />
      )}
      {selectedMetadataVersion?.snapshot.status === 'unavailable' && (
        <Alert type="warning" showIcon message={t('detailsUnavailable')} />
      )}
      {displayedAlgorithm && (
        <AlgorithmInfo
          algorithm={displayedAlgorithm}
          sourcesById={sourcesById}
          mappingsById={mappingsById}
          outputType={outputType}
          showTargetEdit={false}
        />
      )}
    </Flex>
  );
};

const StyledSelect = styled(Select<any>)`
  min-width: 24ch;
  max-width: 238px;
`;
